import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const steps = [
  {
    title: "Register & Login",
    description:
      "Click 'Register' and fill in your details. Then log in using your registered email and password.",
    note: "You can update your password anytime from your profile.",
  },
  {
    title: "Update Your Profile",
    description:
      "Complete your profile with your full name, email, and password. Copy your User ID for future reference.",
    note: "User ID is required when downloading the appointment receipt.",
  },
  {
    title: "Choose a Passport Service",
    description:
      "Select a service like Fresh Passport or Re-issue. Fill in all details as per your Aadhaar, PAN, and other documents. Upload scanned or photocopied documents.",
    note: "All fields are mandatory. Upload all required documents clearly.",
  },
  {
    title: "Make a Payment",
    description:
      "Complete the payment. You'll receive confirmation via SMS. Appointment details will be updated within 24 hours.",
    note: "We will notify you about your appointment date via SMS.",
  },
  {
    title: "Download Appointment Receipt",
    description:
      "Download your appointment receipt using your User ID and Date of Birth.",
    note: "User ID and Date of Birth are required to access the receipt.",
  },
  {
    title: "Name Change on Passport",
    description:
      "To update your name, fill in old name, new name, mobile number, and current address.",
    note: "All fields must be filled for processing a name change.",
  },
  {
    title: "Required Documents",
    description:
      "Download the required documents checklist. Carry originals to the Passport Seva Kendra (PSK) on your appointment day.",
    note: "All original documents must be brought on the scheduled date.",
  },
];

const HowItWorks = () => {
  return (
    <Container>
      <Title2>How It Works</Title2>
      <SubText>
        Follow these simple steps to complete your passport application process
        smoothly and efficiently.
      </SubText>
      <Timeline>
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <MotionStep
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <StepContent $isReverse={index % 2 !== 0}>
                <TextContent>
                  <StepNumber>Step {index + 1}</StepNumber>
                  <Title>{step.title}</Title>
                  <Description>{step.description}</Description>
                  {step.note && <Note>{step.note}</Note>}
                </TextContent>
              </StepContent>
            </MotionStep>
          </React.Fragment>
        ))}
      </Timeline>
    </Container>
  );
};

export default HowItWorks;

// ---------- STYLED COMPONENTS ----------

const Container = styled.section`
  background: transparent;
  max-width: 1300px;
  margin-bottom: 50px;
`;

const Title2 = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  color: #ffffff;
  letter-spacing: 1px;
`;

const SubText = styled.p`
  font-size: 1rem;
  color: #ccc;
  text-align: center;
  margin-bottom: 2rem;
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
`;

const MotionStep = styled(motion.div)`
  position: relative;
`;

// ✅ Use transient prop $isReverse
const StepContent = styled.div`
  display: flex;
  flex-direction: ${({ $isReverse }) => ($isReverse ? "row-reverse" : "row")};
  align-items: center;
  gap: 2rem;
  background: linear-gradient(145deg, #1a1a1a, #121212);
  border: 1px solid #292929;
  padding: 2rem;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  transition: 0.3s ease;

  &:hover {
    transform: translateY(-6px);
    border: 1px solid #646464;
    border-radius: 12px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const TextContent = styled.div`
  flex: 1;
  color: #e8e8e8;
`;

const StepNumber = styled.div`
  background: linear-gradient(to right, #194bfd, #ad13fb);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
  font-weight: bold;
  font-size: 2rem;
  margin-bottom: 0.6rem;
  display: inline-block;

  &::before {
    content: "⇣";
    margin-right: 8px;
    font-size: 1.2rem;
    color: #999;
  }

  &::after {
    content: "⇡";
    margin-left: 8px;
    font-size: 1.2rem;
    color: #999;
  }
`;

const Title = styled.h3`
  font-size: 1.25rem;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

const Description = styled.p`
  font-size: 1rem;
  color: #ccc;
`;

const Note = styled.p`
  font-size: 0.95rem;
  color: #bbbbbb;
  font-style: italic;
  margin-top: 0.5rem;
`;
